import {
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ComponentType,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { newListingSourceAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';
import { useNavigationOptions } from 'app/hooks/navigation';
import { usePageTitle } from 'app/utils/web-helpers';
import { useAllAddresses } from 'app/network/address';
import { useListingVisibilityGroups } from 'app/network/groups';
import { useUser } from 'app/network/user';
import {
  BULK_LISTING_MAX_PHOTOS,
  BULK_LISTING_UPLOAD_CHUNK_SIZE,
  type BulkListingDraft,
  type BulkListingSession,
  isBulkListingGenerationActive,
  useBulkListingSession,
  useCancelBulkListingSession,
  useCreateBulkListingSession,
  useGenerateBulkListingDrafts,
  useRetryBulkListingDraft,
  useSaveBulkListingDraft,
  useUpdateBulkListingDraft,
  useUpdateBulkListingsVisibility,
  useUploadBulkListingPhotos,
} from 'app/network/listing';
import type { NavigationScreen, SpecificNavigationProp } from 'app/types/navigation';
import { ListingSubtype, ListingType } from 'app/types/listing';
import { StorageKey, storage, useStoredValue } from 'app/lib/storage';
import { DynamicFeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import type { ImagePickerAsset } from 'expo-image-picker';
import { useTranslation } from 'react-i18next';
import { Alert, Platform, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { BulkStepHeader } from './bulk-step-header';
import { PhotoSelectionStep } from './photo-selection-step';
import { BulkCameraCapture } from './bulk-camera';
import { BulkGenerationStep } from './bulk-generation';
import { BulkReviewStep } from './bulk-review';
import { BulkCompletionStep } from './bulk-completion';
import { BulkVisibilityStep, type BulkVisibility } from './bulk-visibility';

import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
type BulkStage = 'select' | 'camera' | 'generating' | 'review' | 'visibility' | 'complete';
type DraftResolution = 'saved' | 'skipped';

const reviewableDraftStatuses = new Set<BulkListingDraft['status']>(['generated', 'generation_failed', 'save_failed']);

function sortDrafts(drafts: BulkListingDraft[]) {
  return [...drafts].sort((a, b) => a.position - b.position);
}

function chunkPhotos(photos: ImagePickerAsset[]) {
  const chunks: ImagePickerAsset[][] = [];
  for (let index = 0; index < photos.length; index += BULK_LISTING_UPLOAD_CHUNK_SIZE) {
    chunks.push(photos.slice(index, index + BULK_LISTING_UPLOAD_CHUNK_SIZE));
  }
  return chunks;
}

async function uploadPhotoChunks(
  photos: ImagePickerAsset[],
  sessionId: string,
  upload: ReturnType<typeof useUploadBulkListingPhotos>['mutateAsync'],
) {
  for (const chunk of chunkPhotos(photos)) {
    await upload({ sessionId, photos: chunk });
  }
}

function isSessionDone(session: BulkListingSession) {
  return (
    session.status === 'completed' ||
    session.status === 'cancelled' ||
    session.status === 'expired' ||
    (session.progress.total > 0 && session.progress.saved + session.progress.skipped >= session.progress.total)
  );
}

function isBulkListingGenerationComplete(session: BulkListingSession) {
  const { generationFailed, generated, saveFailed, saved, skipped, total } = session.progress;
  return total > 0 && generated + generationFailed + saved + skipped + saveFailed >= total;
}

function useDefaultBulkVisibility() {
  const { data: allAddresses } = useAllAddresses();
  const { data: allGroups } = useListingVisibilityGroups(true);
  return {
    loaded: Boolean(allAddresses && allGroups),
    addresses: allAddresses ?? [],
    groups: allGroups ?? [],
    addressIds: allAddresses?.map(({ id }) => id) ?? [],
    groupIds: allGroups?.map(({ id }) => id) ?? [],
  };
}

function getInitialBulkSessionId(routeSessionId?: string, storedSessionId?: string | null) {
  return routeSessionId ?? storedSessionId ?? null;
}

function useBulkSessionSelection(initialSessionId: string | null, storedSessionId?: string | null) {
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId);
  const [stage, setStage] = useState<BulkStage>(
    initialSessionId ? 'generating' : Platform.OS === 'web' ? 'select' : 'camera',
  );
  const [handledStoredSessionId, setHandledStoredSessionId] = useState(storedSessionId);

  if (storedSessionId !== handledStoredSessionId) {
    setHandledStoredSessionId(storedSessionId);
    if (!sessionId && storedSessionId) {
      setSessionId(storedSessionId);
      setStage('generating');
    }
  }

  return { sessionId, setSessionId, setStage, stage };
}

function useBulkSessionStageSync({
  isPreparingGeneration,
  session,
  sessionId,
  setStage,
  stage,
}: {
  isPreparingGeneration: boolean;
  session?: BulkListingSession;
  sessionId: string | null;
  setStage: Dispatch<SetStateAction<BulkStage>>;
  stage: BulkStage;
}) {
  const source = JSON.stringify([
    session?.id,
    session?.status,
    session?.progress,
    sessionId,
    isPreparingGeneration,
    stage,
  ]);
  const [handledSource, setHandledSource] = useState<string | null>(null);

  if (source === handledSource) return;
  setHandledSource(source);
  if (session?.id !== sessionId || isPreparingGeneration) return;
  if (stage === 'complete' || stage === 'visibility') return;
  if (isSessionDone(session)) {
    setStage(session.progress.saved > 0 ? 'visibility' : 'complete');
  } else if (stage === 'review' && isBulkListingGenerationActive(session)) {
    setStage('generating');
  }
}

function getOptimisticProgress(
  drafts: BulkListingDraft[],
  optimisticDraftResolutions: Record<string, DraftResolution>,
) {
  let saved = 0;
  let skipped = 0;
  for (const draft of drafts) {
    const resolution = optimisticDraftResolutions[draft.id];
    if (!resolution || !reviewableDraftStatuses.has(draft.status)) continue;
    if (resolution === 'saved') saved += 1;
    else skipped += 1;
  }
  return { saved, skipped };
}

function getReviewProgress(
  session: BulkListingSession | undefined,
  optimisticProgress: { saved: number; skipped: number },
) {
  if (!session?.progress) return undefined;
  return {
    ...session.progress,
    saved: session.progress.saved + optimisticProgress.saved,
    skipped: session.progress.skipped + optimisticProgress.skipped,
  };
}

function getBulkMutationState(
  mutations: { error: Error | null; isPending: boolean }[],
  sessionError: Error | null,
  localError: Error | null,
) {
  return {
    error: mutations.map((mutation) => mutation.error).find((error) => error !== null) ?? sessionError ?? localError,
    isBusy: mutations.some((mutation) => mutation.isPending),
  };
}

function shouldClearBulkSession({
  isPreparingGeneration,
  session,
  sessionId,
  stage,
}: {
  isPreparingGeneration: boolean;
  session?: BulkListingSession;
  sessionId: string | null;
  stage: BulkStage;
}) {
  if (session?.id !== sessionId || isPreparingGeneration) return false;
  return stage === 'complete';
}

function useBulkSessionLifecycle({
  isPreparingGeneration,
  session,
  sessionId,
  setStage,
  shouldClearStoredSession,
  stage,
}: {
  isPreparingGeneration: boolean;
  session?: BulkListingSession;
  sessionId: string | null;
  setStage: Dispatch<SetStateAction<BulkStage>>;
  shouldClearStoredSession: boolean;
  stage: BulkStage;
}) {
  useEffect(() => {
    if (shouldClearStoredSession) void storage.clear(StorageKey.BulkListingSessionId);
  }, [shouldClearStoredSession]);

  useEffect(() => {
    if (
      stage !== 'generating' ||
      isPreparingGeneration ||
      session?.id !== sessionId ||
      !isBulkListingGenerationComplete(session)
    ) {
      return;
    }
    const timeout = setTimeout(() => setStage('review'), 400);
    return () => clearTimeout(timeout);
  }, [isPreparingGeneration, session, sessionId, setStage, stage]);
}

function BulkScreenContent({
  active,
  embedded,
  canUseBulkListingCreation,
  error,
  generationProps,
  isBusy,
  isPreparingGeneration,
  onCameraCancel,
  onCameraDone,
  onCloseComplete,
  onOpenCamera,
  onPhotosChange,
  onStart,
  photos,
  reviewProps,
  session,
  stage,
  visibilityLoaded,
  visibilityProps,
}: {
  canUseBulkListingCreation: boolean;
  active: boolean;
  embedded: boolean;
  error: Error | null;
  generationProps: Pick<ComponentProps<typeof BulkGenerationStep>, 'isCancelling' | 'onCancel' | 'onRetryFailed'>;
  isBusy: boolean;
  isPreparingGeneration: boolean;
  onCameraCancel: () => void;
  onCameraDone: (photos: ImagePickerAsset[]) => void;
  onCloseComplete: () => void;
  onOpenCamera: () => void;
  onPhotosChange: Dispatch<SetStateAction<ImagePickerAsset[]>>;
  onStart: () => void;
  photos: ImagePickerAsset[];
  reviewProps: Omit<ComponentProps<typeof BulkReviewStep>, 'currentDraft' | 'progress'> & {
    currentDraft?: BulkListingDraft;
    progress?: BulkListingSession['progress'];
  };
  session?: BulkListingSession;
  stage: BulkStage;
  visibilityLoaded: boolean;
  visibilityProps: ComponentProps<typeof BulkVisibilityStep>;
}) {
  const pagePadding = usePagePadding();
  if (!canUseBulkListingCreation) return null;
  if (stage === 'camera') {
    return (
      <BulkCameraCapture
        active={active}
        embedded={embedded}
        photos={photos}
        onPhotosChange={onPhotosChange}
        disabled={isBusy || !visibilityLoaded}
        error={error?.message}
        maxPhotos={BULK_LISTING_MAX_PHOTOS}
        onCancel={onCameraCancel}
        onDone={onCameraDone}
      />
    );
  }
  return (
    <View style={{ flex: 1, paddingBottom: pagePadding.paddingBottom }}>
      {error ? <RemoteError error={error} message={error.message} /> : null}
      {stage === 'select' ? (
        <Page keyboardShouldPersistTaps="handled">
          <Column gap="spacing.general.x5">
            <BulkStepHeader stage={stage} />
            <PhotoSelectionStep
              isBusy={isBusy || !visibilityLoaded}
              onOpenCamera={onOpenCamera}
              onContinue={onStart}
              onPhotosChange={onPhotosChange}
              photos={photos}
            />
          </Column>
        </Page>
      ) : null}
      {stage === 'generating' ? (
        <BulkGenerationStep
          {...generationProps}
          active={active}
          photos={photos}
          isPreparing={isPreparingGeneration}
          session={session}
        />
      ) : null}
      {stage === 'review' ? <BulkReviewStep {...reviewProps} /> : null}
      {stage === 'visibility' && visibilityLoaded ? <BulkVisibilityStep {...visibilityProps} /> : null}
      {stage === 'complete' ? <BulkCompletionStep onClose={onCloseComplete} session={session} /> : null}
    </View>
  );
}

const BulkNewContentComponent: ComponentType<
  Omit<ComponentProps<NavigationScreen<'bulkNew'>>, 'navigation'> & {
    navigation: SpecificNavigationProp<'bulkNew' | 'offer-hub' | 'publish'>;
    embedded?: boolean;
    active?: boolean;
  }
> = ({ navigation, route, embedded = false, active = true }) => {
  const { t } = useTranslation('new');
  const { data: currentUser, isLoading: isUserLoading } = useUser();
  const storedNewListingSource = useAtomValue(newListingSourceAtom);
  const resetNewListingSource = useResetAtom(newListingSourceAtom);
  const [newListingSource] = useState(() => route.params?.newListingSource ?? storedNewListingSource);
  const [storedSessionId, setStoredSessionId] = useStoredValue<string | null>(StorageKey.BulkListingSessionId);
  const initialSessionId = getInitialBulkSessionId(route.params?.sessionId, storedSessionId);
  const { sessionId, setSessionId, setStage, stage } = useBulkSessionSelection(initialSessionId, storedSessionId);
  const [photos, setPhotos] = useState<ImagePickerAsset[]>([]);
  const [localError, setLocalError] = useState<Error | null>(null);
  const [isPreparingGeneration, setIsPreparingGeneration] = useState(false);
  const [optimisticDraftResolutions, setOptimisticDraftResolutions] = useState<Record<string, DraftResolution>>({});
  const isStartingNewSession = useRef(false);
  const resolvingDraftIds = useRef(new Set<string>());

  const visibility = useDefaultBulkVisibility();
  const sessionQuery = useBulkListingSession(sessionId, {
    refetchInterval: stage === 'generating' || stage === 'review' || stage === 'visibility' ? 1500 : false,
  });
  const session = sessionQuery.data;
  useBulkSessionStageSync({ isPreparingGeneration, session, sessionId, setStage, stage });

  const createSession = useCreateBulkListingSession();
  const uploadPhotos = useUploadBulkListingPhotos();
  const generateDrafts = useGenerateBulkListingDrafts();
  const updateDraft = useUpdateBulkListingDraft();
  const retryDraft = useRetryBulkListingDraft();
  const saveDraft = useSaveBulkListingDraft();
  const cancelSession = useCancelBulkListingSession();
  const updateVisibility = useUpdateBulkListingsVisibility();

  const canUseBulkListingCreation =
    !isUserLoading && isFeatureEnabled(DynamicFeatureFlag.BulkListingCreation, currentUser?.featureFlags);
  useEffect(() => {
    if (!isUserLoading && !isFeatureEnabled(DynamicFeatureFlag.BulkListingCreation, currentUser?.featureFlags)) {
      navigation.goBack();
    }
  }, [currentUser, isUserLoading, navigation]);

  useNavigationOptions(
    navigation,
    {
      headerShown: stage !== 'camera',
      headerBackVisible: false,
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t('bulk.headerTitle')}
        </Text>
      ),
    },
    embedded,
  );

  const setPageTitle = usePageTitle('new');
  useEffect(() => {
    if (!embedded) setPageTitle('bulk.headerTitle');
  }, [embedded, setPageTitle]);

  const shouldClearStoredSession = shouldClearBulkSession({ isPreparingGeneration, session, sessionId, stage });
  useBulkSessionLifecycle({
    isPreparingGeneration,
    session,
    sessionId,
    setStage,
    shouldClearStoredSession,
    stage,
  });

  const sortedDrafts = sortDrafts(session?.drafts ?? []);
  const reviewableDrafts = sortedDrafts.filter(
    (draft) => reviewableDraftStatuses.has(draft.status) && !optimisticDraftResolutions[draft.id],
  );
  const currentDraft = reviewableDrafts[0];
  const optimisticProgress = getOptimisticProgress(sortedDrafts, optimisticDraftResolutions);
  const reviewProgress = getReviewProgress(session, optimisticProgress);
  const mutationState = getBulkMutationState(
    [createSession, uploadPhotos, generateDrafts, updateDraft, retryDraft, saveDraft, cancelSession, updateVisibility],
    sessionQuery.error,
    localError,
  );

  const startBulkSession = async (selectedPhotos: ImagePickerAsset[] = photos) => {
    if (!visibility.loaded || isStartingNewSession.current) return;
    if (selectedPhotos.length === 0) {
      setLocalError(new Error(t('bulk.errors.noPhotos')));
      return;
    }

    setLocalError(null);
    isStartingNewSession.current = true;
    setIsPreparingGeneration(true);
    setStage('generating');

    const handleStartError = (error: unknown) => {
      setIsPreparingGeneration(false);
      isStartingNewSession.current = false;
      setStage(Platform.OS === 'web' ? 'select' : 'camera');
      setSessionId(null);
      void storage.clear(StorageKey.BulkListingSessionId);
      setLocalError(error instanceof Error ? error : new Error(String(error)));
    };

    let createdSession: Awaited<ReturnType<typeof createSession.mutateAsync>>;
    try {
      createdSession = await createSession.mutateAsync({
        addressIds: visibility.addressIds,
        groupIds: visibility.groupIds,
        type: ListingType.offer,
        subtype: ListingSubtype.object,
        source: newListingSource,
      });
      setSessionId(createdSession.id);
      resetNewListingSource();
      await storage.saveString(StorageKey.BulkListingSessionId, JSON.stringify(createdSession.id));
    } catch (error) {
      handleStartError(error);
      return;
    }

    try {
      await uploadPhotoChunks(selectedPhotos, createdSession.id, uploadPhotos.mutateAsync);
    } catch (error) {
      handleStartError(error);
      return;
    }

    try {
      await generateDrafts.mutateAsync({ sessionId: createdSession.id });
      setIsPreparingGeneration(false);
      setStage('generating');
    } catch (error) {
      handleStartError(error);
    }
  };

  const handleCancel = () => {
    const close = () => {
      void storage.clear(StorageKey.BulkListingSessionId);
      navigation.goBack();
    };

    if (!sessionId) {
      close();
      return;
    }

    Alert.alert(t('bulk.cancel.title'), t('bulk.cancel.description'), [
      { text: t('bulk.cancel.keepWorking'), style: 'cancel' },
      {
        text: t('bulk.cancel.confirm'),
        style: 'destructive',
        onPress: () => {
          cancelSession.mutate(
            { sessionId },
            {
              onSettled: close,
            },
          );
        },
      },
    ]);
  };

  const handleCloseComplete = () => {
    void storage.clear(StorageKey.BulkListingSessionId);
    setStoredSessionId(null);
    setSessionId(null);
    setPhotos([]);
    setOptimisticDraftResolutions({});
    resolvingDraftIds.current.clear();
    isStartingNewSession.current = false;
    setIsPreparingGeneration(false);
    setLocalError(null);
    createSession.reset();
    uploadPhotos.reset();
    generateDrafts.reset();
    updateDraft.reset();
    retryDraft.reset();
    saveDraft.reset();
    cancelSession.reset();
    updateVisibility.reset();
    setStage(Platform.OS === 'web' ? 'select' : 'camera');
    if (!session?.progress.saved) return;
    Toast.show({
      type: 'success',
      position: 'bottom',
      text1: t('bulk.completion.toast'),
    });
  };

  const openBulkCamera = () => {
    setStage('camera');
  };

  const handleBulkCameraDone = (capturedPhotos: ImagePickerAsset[]) => {
    setPhotos(capturedPhotos);
    void startBulkSession(capturedPhotos);
  };

  return (
    <BulkScreenContent
      active={active}
      embedded={embedded}
      canUseBulkListingCreation={canUseBulkListingCreation}
      error={mutationState.error}
      generationProps={{
        isCancelling: cancelSession.isPending,
        onCancel: handleCancel,
        onRetryFailed: () => {
          if (!sessionId || !session) return;
          void generateDrafts.mutateAsync({
            sessionId,
            draftIds: session.drafts.flatMap((draft) => (draft.status === 'generation_failed' ? [draft.id] : [])),
          });
        },
      }}
      isBusy={mutationState.isBusy}
      isPreparingGeneration={isPreparingGeneration}
      onCameraCancel={handleCancel}
      onCameraDone={handleBulkCameraDone}
      onCloseComplete={handleCloseComplete}
      onOpenCamera={openBulkCamera}
      onPhotosChange={setPhotos}
      onStart={() => void startBulkSession()}
      photos={photos}
      reviewProps={{
        currentDraft,
        isBusy: retryDraft.isPending || (!currentDraft && (saveDraft.isPending || updateDraft.isPending)),
        onComplete: () => {
          if (!session || !isSessionDone(session)) return;
          setStage(session.progress.saved > 0 ? 'visibility' : 'complete');
        },
        onRetry: (draftId) => {
          if (!sessionId) return;
          setStage('generating');
          retryDraft.mutate({ sessionId, draftId });
        },
        onSave: (draftId, title, description) => {
          if (!sessionId || resolvingDraftIds.current.has(draftId)) return;
          resolvingDraftIds.current.add(draftId);
          const isLastReviewableDraft = reviewableDrafts.length === 1;
          setOptimisticDraftResolutions((current) => ({ ...current, [draftId]: 'saved' }));
          saveDraft.mutate(
            { sessionId, draftId, title, description },
            {
              onSettled: () => {
                resolvingDraftIds.current.delete(draftId);
              },
              onError: () => {
                setOptimisticDraftResolutions((current) => removeDraftResolution(current, draftId));
                if (isLastReviewableDraft) setStage('review');
              },
            },
          );
        },
        onSkip: (draftId) => {
          if (!sessionId || resolvingDraftIds.current.has(draftId)) return;
          resolvingDraftIds.current.add(draftId);
          const isLastReviewableDraft = reviewableDrafts.length === 1;
          setOptimisticDraftResolutions((current) => ({ ...current, [draftId]: 'skipped' }));
          updateDraft.mutate(
            { sessionId, draftId, status: 'skipped' },
            {
              onSettled: () => {
                resolvingDraftIds.current.delete(draftId);
              },
              onError: () => {
                setOptimisticDraftResolutions((current) => removeDraftResolution(current, draftId));
                if (isLastReviewableDraft) setStage('review');
              },
            },
          );
        },
        progress: reviewProgress,
      }}
      session={session}
      stage={stage}
      visibilityProps={{
        addresses: visibility.addresses,
        groups: visibility.groups,
        isBusy: mutationState.isBusy || !session || !isSessionDone(session),
        onConfirm: (selection: BulkVisibility) => {
          if (!session || !isSessionDone(session) || mutationState.isBusy) return;
          updateVisibility.mutate(
            { listingIds: session.savedListingIds, ...selection },
            { onSuccess: () => setStage('complete') },
          );
        },
      }}
      visibilityLoaded={visibility.loaded}
    />
  );
};

export const BulkNewContent = withErrorBoundary(BulkNewContentComponent);
export const BulkNewScreen: NavigationScreen<'bulkNew'> = BulkNewContent;

function removeDraftResolution(current: Record<string, DraftResolution>, draftId: string) {
  return Object.fromEntries(Object.entries(current).filter(([candidateId]) => candidateId !== draftId));
}
